import * as React from 'react';
import type {MetaAttributes} from '@packages/systems/wfdl';
import {emotionStyled} from '@packages/utilities/styled';
import {
  Note,
  NoteBody,
  NoteActions,
  NoteButton,
  SwapIcon,
} from '@packages/systems/spring';
import {useTranslation} from '@packages/systems/designer-translations';

/**
 * Context for slot migration actions.
 * Provides callbacks for "Convert" and "Delete" actions in the SlotMetaUI.
 * - onDelete: Unbinds the slot binding (used in DevlinkSettings context)
 * - onDeleteSlotProperty: Deletes the slot property entirely (used in PropsEditor context)
 */
type SlotMigrationContextType = {
  onConvert: (() => void) | null;
  onDeleteSlotProperty: (() => void) | null;
};

const defaultSlotMigrationContext: SlotMigrationContextType = {
  onConvert: null,
  onDeleteSlotProperty: null,
};

export const SlotMigrationContext =
  React.createContext<SlotMigrationContextType>(defaultSlotMigrationContext);

// Wrapper to break out of the two-column grid layout used by PropsMetaSettings
const FullWidthWrapper = emotionStyled('div')({
  gridColumn: '1 / -1',
});

/**
 * MetaUI component for deprecated Devlink::Slot type.
 *
 * Displays a deprecation warning with "Convert" and "Delete" buttons.
 * The buttons use the SlotMigrationContext for their click handlers.
 *
 * - Convert: Transforms the deprecated DevLink slot to a native component slot
 * - Delete: Deletes the DevLink slot property from the component
 */
export const SlotMetaUI = ({
  meta: _meta,
  onChange: _onChange,
}: {
  meta: MetaAttributes | null;
  onChange: (arg1: MetaAttributes) => void;
}) => {
  const t = useTranslation();
  const {onConvert, onDeleteSlotProperty} =
    React.useContext(SlotMigrationContext);

  return (
    <FullWidthWrapper>
      <Note
        icon={SwapIcon}
        colorVariant="default"
        data-automation-id="deprecated-slot-meta-ui"
      >
        <NoteBody>
          {t('designer.components.props.devlink-slot-deprecated-tooltip')}
        </NoteBody>
        <NoteActions>
          {onConvert != null && (
            <NoteButton
              onClick={onConvert}
              data-automation-id="deprecated-slot-convert-button"
            >
              {t('designer.components.props.devlink-slot.convert-button')}
            </NoteButton>
          )}
          <NoteButton
            variant="outline"
            onClick={onDeleteSlotProperty ?? undefined}
            data-automation-id="deprecated-slot-delete-button"
          >
            {t('designer.components.props.devlink-slot.delete-button')}
          </NoteButton>
        </NoteActions>
      </Note>
    </FullWidthWrapper>
  );
};
